/**
 * Client for the PUBLIC (unauthenticated) self-book link endpoints (tv-2na.8.4).
 *
 * The customer opens the artist's link with NO staff login, so the page loads and
 * pays through /api/public/self-book/:token — never the authed /api/bootstrap or
 * /api/intents (which 401 for a customer). The server looks the link up by token,
 * prices it (the pinned hold wins, else the artist's rate card) and runs the
 * atomic create-and-pay; the page only ever shows what the server says.
 *
 * One clock (tv-2na.10.5): both calls send the facilitator's sim clock as
 * `nowIso`; the server honours it only in a demo/dev deployment.
 */
import { useSimulationStore } from '@/state/simulationStore';
import type { SelfBookSession } from '@/domain/selfBookFlow';

export type SelfBookLinkState = 'open' | 'expired' | 'used';

export interface PublicSelfBookBundle {
  /** D05-11: a NAMED link's own customer, for the details step to pre-fill (null on a universal link). */
  prefill?: { name: string; mobile: string; email: string } | null;
  link: {
    state: SelfBookLinkState;
    universal: boolean;
    dateRangeStart: string | null;
    dateRangeEnd: string | null;
    pinned: { startIso: string; sessionLength: SelfBookSession; amountAud: number } | null;
    /** B7: staff ticked "Happy to come in earlier" on the link; the wizard starts ticked. */
    preferBroughtForward?: boolean;
  };
  artist: {
    id: string;
    firstName: string;
    displayName: string;
    avatarUrl: string | null;
    availability: {
      pattern: Partial<Record<'sun' | 'mon' | 'tue' | 'wed' | 'thu' | 'fri' | 'sat', { startTime: string; endTime: string } | null>>;
      rdoDates: string[];
      leaveBlocks: Array<{ startDate: string; endDate: string }>;
      nightSessions: boolean;
    };
  };
  timeZone: string;
  /** The deposit the server charges per session on an unpinned link (rate card). */
  prices: Record<SelfBookSession, number>;
  /** The artist's FIRM bookings only — time ranges, no customer data. */
  busy: Array<{ start: string; durationMin: number }>;
  /** Set when this link (or the receipt passed) is already paid. */
  confirmed: SelfBookConfirmation | null;
  nowIso: string;
}

export interface SelfBookConfirmation {
  startIso: string;
  amountAud: number;
  artistFirstName: string;
}

function simNowIso(): string {
  return useSimulationStore.getState().clockIso;
}

/** Load a link's page bundle. null = unknown link (404); throws on transport errors. */
export async function fetchPublicSelfBook(
  token: string,
  opts: { receipt?: string } = {},
): Promise<PublicSelfBookBundle | null> {
  const qs = new URLSearchParams({ nowIso: simNowIso() });
  if (opts.receipt) qs.set('receipt', opts.receipt);
  const res = await fetch(`/api/public/self-book/${encodeURIComponent(token)}?${qs.toString()}`);
  if (res.status === 404) return null;
  if (!res.ok) throw new Error(`public-self-book ${res.status}`);
  return (await res.json()) as PublicSelfBookBundle;
}

export interface SelfBookPayRequest {
  /** One per checkout attempt (idempotency key): a retry replays, never re-charges. */
  attemptId: string;
  startIso: string;
  session?: SelfBookSession;
  details: { name: string; email: string; mobile: string };
  designBrief: string;
  referencePhotos: string[];
  /** The amount the page showed; the server refuses (409 price_changed) if it differs. */
  quotedAmountAud: number;
  /** B7 (8xat D08-09): "Happy to come in earlier if a slot opens". */
  preferBroughtForward?: boolean;
}

export type SelfBookPayResult =
  | ({ ok: true; alreadyPaid: boolean; receiptId: string } & SelfBookConfirmation)
  | { ok: false; status: number; error: string; code?: string; amountAud?: number };

/** Pay. Never throws — returns a discriminated result the page branches on. */
export async function payPublicSelfBook(token: string, req: SelfBookPayRequest): Promise<SelfBookPayResult> {
  let res: Response;
  try {
    res = await fetch(`/api/public/self-book/${encodeURIComponent(token)}/pay`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ ...req, nowIso: simNowIso() }),
    });
  } catch {
    return { ok: false, status: 0, error: 'network' };
  }
  const body = (await res.json().catch(() => ({}))) as Record<string, unknown>;
  if (res.ok && body.ok === true) {
    return {
      ok: true,
      alreadyPaid: body.alreadyPaid === true,
      receiptId: String(body.receiptId),
      startIso: String(body.startIso),
      amountAud: Number(body.amountAud),
      artistFirstName: String(body.artistFirstName ?? ''),
    };
  }
  return {
    ok: false,
    status: res.status,
    error: typeof body.error === 'string' ? body.error : `http-${res.status}`,
    ...(typeof body.code === 'string' ? { code: body.code } : {}),
    ...(typeof body.amountAud === 'number' ? { amountAud: body.amountAud } : {}),
  };
}
